<template>
  <aside
    class="analysis-side-menu"
    :class="{ 'analysis-side-menu--open': open }"
    :aria-label="t('analysis.sideMenu.ariaLabel')"
    data-testid="analysis-side-menu"
  >
    <!-- Mobile collapse handle -->
    <button
      v-if="$vuetify.display.smAndDown"
      type="button"
      class="side-menu-toggle"
      :aria-expanded="open"
      :aria-controls="menuId"
      data-testid="analysis-side-menu-toggle"
      @click="open = !open"
    >
      <v-icon :icon="open ? 'mdi-close' : 'mdi-menu'" size="20" />
      <span class="side-menu-toggle-label">{{ open ? t('analysis.sideMenu.close') : t('analysis.sideMenu.open') }}</span>
    </button>

    <nav :id="menuId" class="side-menu-body" :hidden="!open && $vuetify.display.smAndDown">
      <!-- Primary product areas — Hub deliberately omitted. Hub is the
           soft post-upload landing surface (chooser between 360 and
           Studio); once the user has picked a direction, returning to a
           chooser-screen breaks the work-in-progress narrative. They can
           still reach it via browser back or by uploading a new plan. -->
      <ul class="side-menu-section" data-testid="analysis-side-menu-products">
        <li class="side-menu-item">
          <router-link
            class="side-menu-link side-menu-link--primary"
            :to="report360Route"
            :class="{ 'side-menu-link--active': mode === '360' }"
            data-testid="analysis-side-menu-360"
            @click="onLinkClick"
          >
            <v-icon icon="mdi-view-dashboard-outline" size="20" />
            <span>{{ t('analysis.productNav.report') }}</span>
          </router-link>
          <ul
            v-if="mode === '360'"
            class="side-menu-subsection"
            data-testid="analysis-side-menu-360-tabs"
            role="tablist"
          >
            <li
              v-for="(tab, idx) in reportTabs"
              :key="`tab-${idx}`"
              class="side-menu-subitem"
            >
              <button
                type="button"
                role="tab"
                :aria-selected="activeTab === idx ? 'true' : 'false'"
                class="side-menu-sublink"
                :class="{ 'side-menu-sublink--active': activeTab === idx }"
                :data-testid="`analysis-side-menu-tab-${idx}`"
                @click="onTabSelect(idx)"
              >
                <v-icon :icon="tab.icon" size="16" />
                <span>{{ tab.title }}</span>
              </button>
            </li>
          </ul>
        </li>

        <li class="side-menu-item">
          <router-link
            class="side-menu-link side-menu-link--primary"
            :to="studioRoute"
            :class="{ 'side-menu-link--active': mode === 'studio' }"
            data-testid="analysis-side-menu-studio"
            @click="onLinkClick"
          >
            <v-icon icon="mdi-auto-fix" size="20" />
            <span>{{ t('analysis.productNav.studio') }}</span>
          </router-link>

          <template v-if="mode === 'studio'">
            <!-- Studio tree -->
            <ul
              v-if="orderedNodes.length > 0"
              class="side-menu-subsection side-menu-subsection--studio-tree"
              data-testid="analysis-side-menu-studio-tree"
            >
              <li
                v-for="node in orderedNodes"
                :key="node.id"
                class="side-menu-subitem"
              >
                <button
                  type="button"
                  class="side-menu-sublink side-menu-sublink--studio-node"
                  :class="{
                    'side-menu-sublink--active': node.id === currentNodeId,
                    'side-menu-sublink--root': node.parentNodeId === null,
                  }"
                  :disabled="treeBusy"
                  :data-node-id="node.id"
                  :data-testid="`analysis-side-menu-studio-node-${node.id}`"
                  :style="{ paddingLeft: `${10 + Math.min(node.depth || 0, 5) * 12}px` }"
                  @click="$emit('select-node', { nodeId: node.id })"
                >
                  <v-icon
                    :icon="node.parentNodeId === null ? 'mdi-floor-plan' : 'mdi-shape-outline'"
                    size="14"
                  />
                  <span class="side-menu-sublink-label">{{ nodeLabel(node) }}</span>
                </button>
              </li>
            </ul>

            <!-- Studio actions. Navigation between concepts happens in the
                 tree above; the only action left here is the branch export. -->
            <ul class="side-menu-subsection side-menu-subsection--studio-actions">
              <li v-if="canExportBranch" class="side-menu-subitem">
                <button
                  type="button"
                  class="side-menu-sublink"
                  :disabled="treeBusy || exportBusy"
                  data-testid="analysis-side-menu-studio-export"
                  @click="$emit('studio-export-branch')"
                >
                  <v-icon icon="mdi-file-pdf-box" size="16" />
                  <span>{{ t('analysis.studio.nav.exportBranch') }}</span>
                </button>
              </li>
            </ul>
          </template>
        </li>
      </ul>

      <!-- Analysis-scoped global actions. Saved-analyses list moved
           to the dedicated /meine-analysen dashboard; credits + sign
           out + Meine Analysen now live in <AppAccountRail> below so
           the user has exactly one entry per account action. -->
      <ul class="side-menu-section side-menu-section--global">
        <li class="side-menu-item">
          <button
            type="button"
            class="side-menu-link side-menu-link--secondary"
            :disabled="isUploading"
            data-testid="analysis-side-menu-upload-new"
            @click="emitAndClose('upload-new')"
          >
            <v-icon icon="mdi-upload" size="18" />
            <span>{{ t('analysis.uploadNew') }}</span>
          </button>
        </li>
        <li v-if="show360PdfExport" class="side-menu-item">
          <button
            type="button"
            class="side-menu-link side-menu-link--secondary"
            :disabled="!canExportPdf || !hasPremiumAccess || exportPdfLoading"
            data-testid="analysis-side-menu-export-pdf-360"
            @click="emitAndClose('export-pdf-360')"
          >
            <v-icon icon="mdi-file-pdf-box" size="18" />
            <span>{{ t('analysis.exportPdf') }}</span>
          </button>
        </li>
      </ul>

      <AppAccountRail />
    </nav>
  </aside>
</template>

<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useDisplay } from 'vuetify';
import AppAccountRail from '@/components/AppAccountRail.vue';

const props = defineProps({
  analysisId: { type: String, default: 'current' },
  mode: { type: String, required: true }, // 'hub' | '360' | 'studio'
  // 360 sub-tabs
  reportTabs: { type: Array, default: () => [] },
  activeTab: { type: Number, default: 0 },
  // Studio tree
  orderedNodes: { type: Array, default: () => [] },
  currentNodeId: { type: String, default: null },
  treeBusy: { type: Boolean, default: false },
  canExportBranch: { type: Boolean, default: false },
  exportBusy: { type: Boolean, default: false },
  // Global controls
  isUploading: { type: Boolean, default: false },
  show360PdfExport: { type: Boolean, default: false },
  canExportPdf: { type: Boolean, default: false },
  hasPremiumAccess: { type: Boolean, default: false },
  exportPdfLoading: { type: Boolean, default: false },
});

const emit = defineEmits([
  'navigate-tab',
  'select-node',
  'studio-export-branch',
  'upload-new',
  'export-pdf-360',
]);

const { t } = useI18n();
const display = useDisplay();
const menuId = `analysis-side-menu-${Math.random().toString(36).slice(2, 8)}`;

// Mobile drawer: collapsed by default; desktop always visible.
const open = ref(false);

const routeId = computed(() => props.analysisId || 'current');
// hubRoute was dropped along with the Hub side-menu entry; it stays as
// the auto-landing surface after a fresh upload but is no longer a
// primary nav target. Kept here only as documentation should the
// product team change their mind.
const report360Route = computed(() => ({ name: 'Analysis360', params: { id: routeId.value } }));
const studioRoute = computed(() => ({ name: 'AnalysisStudio', params: { id: routeId.value } }));

function emitAndClose(event) {
  emit(event);
  if (display.smAndDown.value) open.value = false;
}
function onLinkClick() {
  if (display.smAndDown.value) open.value = false;
}
function onTabSelect(idx) {
  emit('navigate-tab', idx);
  if (display.smAndDown.value) open.value = false;
}

// Helpers
function nodeLabel(node) {
  if (node.parentNodeId === null) return t('analysis.studio.nav.rootLabel');
  const directive = node.directive || t('analysis.studio.nav.unnamed');
  // Truncate so the menu stays narrow.
  if (directive.length > 48) return `${directive.slice(0, 45)}…`;
  return directive;
}
</script>

<style scoped>
.analysis-side-menu {
  --side-menu-bg: #faf6f0;
  --side-menu-fg: #1f3a44;
  --side-menu-fg-muted: #55717c;
  --side-menu-accent: #245346;
  --side-menu-accent-soft: #edf4f1;
  --side-menu-border: rgba(36, 83, 70, 0.10);
  --side-menu-active-bg: #edf4f1;
  --side-menu-active-fg: #245346;
  position: sticky;
  top: 80px;
  align-self: flex-start;
  width: 250px;
  flex-shrink: 0;
  background: var(--side-menu-bg);
  border: 1px solid var(--side-menu-border);
  border-radius: 12px;
  padding: 12px 8px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  box-shadow: 0 8px 24px rgba(24, 39, 48, 0.04);
}

.side-menu-toggle {
  display: none;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 8px;
  background: var(--side-menu-accent-soft);
  color: var(--side-menu-active-fg);
  font-weight: 600;
  font-size: 0.85rem;
}
.side-menu-toggle-label { white-space: nowrap; }

.side-menu-body {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.side-menu-section {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.side-menu-section--global {
  margin-top: auto;
  padding-top: 12px;
  border-top: 1px solid var(--side-menu-border);
}

.side-menu-item {
  display: flex;
  flex-direction: column;
}

.side-menu-link {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 9px 12px;
  border-radius: 8px;
  color: var(--side-menu-fg);
  text-decoration: none;
  font-weight: 600;
  font-size: 0.92rem;
  background: transparent;
  border: none;
  cursor: pointer;
  text-align: left;
  width: 100%;
  transition: background-color 120ms ease, color 120ms ease;
}
.side-menu-link:hover:not(:disabled) {
  background: rgba(36, 83, 70, 0.06);
}
.side-menu-link:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.side-menu-link--primary {
  font-weight: 700;
}
.side-menu-link--secondary {
  font-weight: 500;
  color: var(--side-menu-fg-muted);
  font-size: 0.88rem;
}
.side-menu-link--credits {
  color: #6d3b1f;
}
.side-menu-link--active {
  background: var(--side-menu-active-bg);
  color: var(--side-menu-active-fg);
  box-shadow: inset 0 0 0 1px rgba(36, 83, 70, 0.16);
}

.side-menu-subsection {
  list-style: none;
  padding: 4px 0 4px 8px;
  margin: 4px 0 8px 6px;
  border-left: 1px solid var(--side-menu-border);
  display: flex;
  flex-direction: column;
  gap: 1px;
}
.side-menu-subsection--studio-tree {
  max-height: 220px;
  overflow-y: auto;
}
.side-menu-subsection--studio-actions {
  border-left-color: rgba(109, 59, 31, 0.18);
}

.side-menu-subitem { display: flex; }

.side-menu-sublink {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px;
  border-radius: 6px;
  color: var(--side-menu-fg-muted);
  font-size: 0.82rem;
  font-weight: 500;
  background: transparent;
  border: none;
  cursor: pointer;
  text-align: left;
  width: 100%;
  min-width: 0;
}
.side-menu-sublink:hover:not(:disabled) {
  background: rgba(36, 83, 70, 0.05);
  color: var(--side-menu-fg);
}
.side-menu-sublink:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}
.side-menu-sublink--active {
  background: var(--side-menu-active-bg);
  color: var(--side-menu-active-fg);
  font-weight: 700;
}
.side-menu-sublink--studio-node {
  font-size: 0.78rem;
}
.side-menu-sublink--root {
  font-weight: 700;
}
.side-menu-sublink-label {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Mobile: drawer-like collapse */
@media (max-width: 960px) {
  .analysis-side-menu {
    position: relative;
    top: auto;
    width: 100%;
    padding: 8px;
  }
  .side-menu-toggle {
    display: inline-flex;
    width: max-content;
  }
  .side-menu-body[hidden] {
    display: none;
  }
  .analysis-side-menu--open .side-menu-body {
    padding-top: 8px;
  }
}
</style>
